/**
 * The Hour Optimizer's extra drops for the settings being edited — the
 * planner's own placement (shared `hourTryOrder` + `planHourExtras`), run on
 * the draft so the editor shows them live, and why fewer than asked fit
 * (user, 12/10/2026: "easy to see easy to understand there's no secret").
 * For Media Devoted | Created By Ciro Autiero
 */
import { type CountryWindow, type DropGenHourBucket, type HourExtra, type DropGenRule, type HourReductionResult, reducedHours, reduceHourSlots, hourTryOrder, inCountryWindow, planHourExtras } from '@trafficpulse/shared';

/** The container's day as the planner sees it: [start, end] (end = its end, else its last drop), its drops, the window. */
export interface ExtrasSpan {
  startMin: number;
  endMin: number;
  slotMinutes: number[];
  window: CountryWindow | null;
}

export interface ExtrasSettings {
  minIntervalMin: number;
  maxExtraSlots: number;
}

export interface ExtrasPreview {
  /** The extra drops, in time order. */
  extras: HourExtra[];
  /** The daily test drop in an hour not ranked yet, when one has room. */
  probe: HourExtra | null;
  max: number;
  gap: number;
  /** The most room (min) a minute of the hours it may use has from its drops; null = it may use none. */
  room: number | null;
  /** Hours it may use: ranked, no rule keeping extras out, some minute inside its hours and the window. */
  usableHours: number;
  reductions?: HourReductionResult[];
  keptRegularMinutes?: number[];
}

const dayHour = (m: number) => Math.floor((((m % 1440) + 1440) % 1440) / 60);

/** Like the planner: no span without a start, nor without an end or a drop to end on. */
export function extrasSpan(startMin: number | null, endAtMin: number | null, slotMinutes: number[], window: CountryWindow | null): ExtrasSpan | null {
  const endMin = endAtMin ?? (slotMinutes.length > 0 ? Math.max(...slotMinutes) : null);
  return startMin === null || endMin === null ? null : { startMin, endMin, slotMinutes, window };
}

/** For each hour with a minute in [start, end] and the window: the most room one of its minutes has from the drops. */
export function roomByHour(span: ExtrasSpan): Map<number, number> {
  const out = new Map<number, number>();
  for (let m = span.startMin; m <= span.endMin; m += 1) {
    if (span.window && !inCountryWindow(m, span.window)) continue;
    const room = span.slotMinutes.reduce((r, t) => Math.min(r, Math.abs(t - m)), Infinity);
    out.set(dayHour(m), Math.max(out.get(dayHour(m)) ?? 0, room));
  }
  return out;
}

/** The most room any minute of its day has from its drops — null when no minute is inside the window. */
export function largestRoom(span: ExtrasSpan): number | null {
  const rooms = [...roomByHour(span).values()];
  return rooms.length > 0 ? Math.max(...rooms) : null;
}

export function previewExtras(buckets: ReadonlyArray<DropGenHourBucket>, span: ExtrasSpan, s: ExtrasSettings, rules: readonly DropGenRule[] = []): ExtrasPreview {
  const { ranked, unranked } = hourTryOrder(buckets);
  const placed = planHourExtras({ startMin: span.startMin, endMin: span.endMin, regularMinutes: span.slotMinutes, window: span.window, ...s, ranked, unranked });
  const regular = span.slotMinutes.map((minute, i) => ({ slotId: `regular-${i}`, minute }));
  const extra = placed.map((e, i) => ({ ...e, slotId: `extra-${i}` }));
  const reductions = reduceHourSlots([...regular, ...extra].filter((s) => s.minute >= span.startMin && s.minute <= span.endMin && (!span.window || inCountryWindow(s.minute, span.window))), reducedHours(buckets, rules));
  const removed = new Set(reductions.flatMap((r) => r.removedSlotIds));
  const kept = extra.filter((s) => !removed.has(s.slotId));
  const rooms = roomByHour(span);
  const usable = ranked.filter((h) => rooms.has(h));
  return {
    extras: kept.filter((e) => e.kind === 'extra').map(({ slotId: _id, ...e }) => e),
    probe: kept.filter((e) => e.kind === 'probe').map(({ slotId: _id, ...e }) => e)[0] ?? null,
    reductions,
    keptRegularMinutes: regular.filter((s) => !removed.has(s.slotId)).map((s) => s.minute),
    max: s.maxExtraSlots,
    gap: s.minIntervalMin,
    room: usable.length > 0 ? Math.max(...usable.map((h) => rooms.get(h)!)) : null,
    usableHours: usable.length,
  };
}

/** Every drop it adds, as minutes — what two previews are compared on. */
export const addedMinutes = (p: ExtrasPreview): string => [...p.extras, ...(p.probe ? [p.probe] : [])].map((e) => e.minute).join(',');
